/**
 * Renderizador mínimo de markdown (##, ###, listas, **negrito**, parágrafos).
 * O conteúdo vem do próprio admin/IA, então não há necessidade de sanitização
 * pesada — mesmo assim, nada aqui injeta HTML cru.
 */
import type { ReactNode } from 'react';

function inline(text: string, key: number) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <span key={key}>
      {parts.map((p, i) =>
        p.startsWith('**') && p.endsWith('**') ? (
          <strong key={i} className="font-semibold text-slate-100">
            {p.slice(2, -2)}
          </strong>
        ) : (
          p
        ),
      )}
    </span>
  );
}

export function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  let key = 0;

  const flushList = () => {
    if (list.length === 0) return;
    blocks.push(
      <ul key={key++} className="my-2 list-disc space-y-1 pl-5 text-sm text-slate-300">
        {list.map((item, i) => (
          <li key={i}>{inline(item, i)}</li>
        ))}
      </ul>,
    );
    list = [];
  };

  for (const raw of text.split('\n')) {
    const line = raw.trimEnd();
    if (/^\s*[-*]\s+/.test(line)) {
      list.push(line.replace(/^\s*[-*]\s+/, ''));
      continue;
    }
    flushList();
    if (line.startsWith('### ')) {
      blocks.push(
        <h4 key={key++} className="mt-4 mb-1 text-sm font-semibold text-slate-100">
          {line.slice(4)}
        </h4>,
      );
    } else if (line.startsWith('## ')) {
      blocks.push(
        <h3 key={key++} className="mt-5 mb-2 text-base font-semibold text-slate-100">
          {line.slice(3)}
        </h3>,
      );
    } else if (line.startsWith('# ')) {
      blocks.push(
        <h2 key={key++} className="mt-5 mb-2 text-lg font-bold text-slate-100">
          {line.slice(2)}
        </h2>,
      );
    } else if (line.trim() !== '') {
      blocks.push(
        <p key={key++} className="my-2 text-sm leading-relaxed text-slate-300">
          {inline(line, key)}
        </p>,
      );
    }
  }
  flushList();
  return <div>{blocks}</div>;
}
